/**
 * AdoptionData — data provider for the Admin → Adoption analytics feature.
 *
 * Source of truth is the live backend, not the standalone SPA's S3 snapshots:
 *   - events:           GET  /api/usage/fetch?days&include_prompts=true&raw_events=true
 *                       (must return per-event rows with user, timestamp, prompt
 *                        text/messages, and ideally source + status + latency).
 *                       If the endpoint can't yet return raw events, the backend
 *                       should expose /api/usage/events backed by the SAME Graylog
 *                       query the SPA uses (see GRAYLOG_QUERY below) — the browser
 *                       can't hit Graylog directly (token + CORS).
 *   - classifications:  POST /api/usage/categorize  (Bedrock-backed, cached).
 *
 * Exposes the same shape the ported views expect: { events, classMap, aliases,
 * mergeMerck, ... } plus company-selection state for the Companies drill-down.
 */
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { RequestBuilder } from '../../../queries/request';
import { getConfig } from '../../../lib/configMgr';
import { normalizeEvents, DEFAULT_COMPANY_ALIASES } from '../../../lib/adoptionAnalytics';

const { ExternalRESTAPI } = getConfig();

// The reviewed external-user query (kept in sync with the SPA ingest); used by a
// Graylog-backed events endpoint if /api/usage can't produce raw events.
export const GRAYLOG_QUERY =
  'source:rest-api* AND _exists_:user AND NOT user:anonymous AND NOT user:system ' +
  'AND NOT user:/@.*plexresearch\\.com/ AND NOT user:/@gmail.com/ ' +
  'AND (_exists_:messages OR _exists_:initial_prompt)';

const Ctx = createContext(null);
export const useData = () => useContext(Ctx);

async function fetchEvents(authData, days) {
  // Prefer /api/usage/fetch with raw events; fall back to a dedicated
  // Graylog-backed /api/usage/events endpoint if configured.
  const tryUrl = (base) => {
    const url = new URL(base, window.location.origin);
    url.searchParams.set('days', String(days));
    url.searchParams.set('include_prompts', 'true');
    url.searchParams.set('raw_events', 'true');
    return url;
  };
  const usageUrl = tryUrl(ExternalRESTAPI.UsageFetch);
  const resp = await new RequestBuilder(usageUrl).withAuthorization(authData).fetch();
  const data = await resp.json();
  let events = normalizeEvents(data);
  if (events.length === 0 && ExternalRESTAPI.UsageEvents) {
    // /api/usage couldn't supply raw events — use the Graylog-backed endpoint.
    const evUrl = tryUrl(ExternalRESTAPI.UsageEvents);
    evUrl.searchParams.set('query', GRAYLOG_QUERY);
    const r2 = await new RequestBuilder(evUrl).withAuthorization(authData).fetch();
    events = normalizeEvents(await r2.json());
  }
  return events;
}

async function classify(authData, texts) {
  if (!ExternalRESTAPI.UsageCategorize) return {};
  const url = new URL(ExternalRESTAPI.UsageCategorize, window.location.origin);
  const prompts = texts.map((t, i) => ({ id: i, text: t.slice(0, 600) }));
  const resp = await new RequestBuilder(url)
    .withAuthorization(authData)
    .withJSONContentType()
    .asPOST(JSON.stringify({ prompts }))
    .fetch();
  if (!resp.ok) { const e = new Error(`categorize ${resp.status}`); e.code = resp.status; throw e; }
  const res = await resp.json();
  const map = {};
  (res.results || []).forEach((r) => { const t = texts[r.id]; if (t != null) map[t] = { areas: r.areas || [], uses: r.uses || [] }; });
  return map;
}

export function AdoptionDataProvider({ authData, children }) {
  const [days, setDays] = useState(180);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mergeMerck, setMergeMerck] = useState(true);
  const [classMap, setClassMap] = useState({});
  const [classState, setClassState] = useState('idle'); // idle|loading|done|unconfigured|error
  const [selectedCompany, setSelectedCompany] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError('');
    fetchEvents(authData, days)
      .then((ev) => { if (!cancelled) { setEvents(ev); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(String(e.message || e)); setLoading(false); } });
    return () => { cancelled = true; };
  }, [authData, days]);

  useEffect(() => {
    if (!events.length) { setClassMap({}); setClassState('idle'); return; }
    let cancelled = false;
    const texts = [...new Set(events.map((e) => e.text))];
    setClassState('loading');
    classify(authData, texts)
      .then((m) => { if (!cancelled) { setClassMap(m); setClassState('done'); } })
      .catch((e) => { if (!cancelled) { setClassMap({}); setClassState(e.code === 404 ? 'unconfigured' : 'error'); } });
    return () => { cancelled = true; };
  }, [authData, events]);

  const aliases = useMemo(() => (mergeMerck ? DEFAULT_COMPANY_ALIASES : {}), [mergeMerck]);

  const value = {
    loading, error, events, classMap, classState, aliases,
    mergeMerck, setMergeMerck, days, setDays,
    selectedCompany, setSelectedCompany,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
